import { VectorLabel } from "@evergis/sgis/es/renders/VectorLabel";
import React from "react";
import { Render, VectorRender } from "@evergis/sgis/es/renders/Render";
import { StaticVectorImageRender } from "@evergis/sgis/es/renders/StaticVectorImageRender";
import { Canvas } from "@evergis/sgis/es/painters/DomPainter/Canvas";
import { painter } from "../../../evergis/painter";
import { getSymbolRenderers, SymbolType, FeatureToRender, getFeatureToRender } from "./getSymbolRenderers";
import { formatSymbol } from "./formatSymbol";

type RenderForCanvas = VectorRender | StaticVectorImageRender | VectorLabel;

export type SymbolRenderProps = {
  className?: string;
  symbol: SymbolType;
  size?: number;
  isLegend?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  asyncMount?: boolean;
  feature?: FeatureToRender;
  translateX?: number;
  translateY?: number;
} & React.CanvasHTMLAttributes<HTMLCanvasElement>;

const DEFAULT_SIZE = 16;

export const renderSymbolToCanvas = async (
  canvas: Canvas,
  symbol: SymbolType,
  size: number,
  feature?: FeatureToRender,
  forExport = false,
) => {
  const renderers = getSymbolRenderers(symbol, size, feature);
  const imageLoadedRenderers = (await waitImageLoad(renderers)) || [];

  imageLoadedRenderers.forEach(renderer => {
    // Используется только для легенды в экспорте изображения карты, поэтому DomPainter не нужен
    // For export/print - don't use painter. For UI legend - use painter for proper rendering
    if (forExport) {
      canvas.draw(renderer as RenderForCanvas);
    } else {
      canvas.draw(renderer as RenderForCanvas, painter);
    }
  });
};

export class SymbolRenderer extends React.PureComponent<SymbolRenderProps> {
  static defaultProps = {
    size: DEFAULT_SIZE,
  };

  public canvas?: Canvas;

  public initCanvas = (canvasNode?: HTMLCanvasElement) => {
    if (canvasNode) {
      this.canvas = new Canvas(canvasNode);
    }
  };

  public renderSymbol = async () => {
    const { symbol: symbolFromProps, size, isLegend, maxWidth, maxHeight, feature } = this.props;
    const symbol = await formatSymbol(symbolFromProps);

    this.canvas.node.height = maxHeight ? Math.min(size, maxHeight) : size;
    this.canvas.node.width = maxWidth ? Math.min(size, maxWidth) : size;

    renderSymbolToCanvas(this.canvas, symbol, size, feature || getFeatureToRender(symbol, isLegend));
  };

  public componentDidUpdate() {
    this.renderSymbol();
  }

  public componentDidMount() {
    if (this.props.asyncMount) {
      setTimeout(this.renderSymbol);
    } else {
      this.renderSymbol();
    }
  }

  public render() {
    const { symbol, isLegend, maxWidth, maxHeight, asyncMount, feature, translateX, translateY, ...props } = this.props;

    return <canvas {...props} ref={this.initCanvas} />;
  }
}

export function waitImageLoad(renders: Render[] = []): Promise<Render[]> {
  const promises = [];

  renders.forEach(render => {
    if (render instanceof StaticVectorImageRender && render.node instanceof HTMLImageElement && !render.node.complete) {
      promises.push(waitNodeLoad(render.node));
    }
  });

  return Promise.all(promises).then(() => renders);
}

function waitNodeLoad(node: HTMLImageElement): Promise<void> {
  return new Promise(res => {
    const handler = () => {
      node.removeEventListener("load", handler);
      node.removeEventListener("error", handler);
      res();
    };

    node.addEventListener("load", handler);
    node.addEventListener("error", handler);
  });
}
